import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useEnrichmentJobPolling from '../../../hooks/useEnrichmentJobPolling';
import { buildRevealPollMessage } from '../adapters/services/EnrichmentTagService';
import {
  eventAgentEnrichmentRevealJobStarted,
  eventAgentEnrichmentRevealJobCompleted
} from '../../../ga4/ga4';

/**
 * Reveal progress card — single or bulk (TD-2293, shipped TD-2281 contract).
 *
 * Rendered for a `[REVEAL_STARTED:<jobId>]` turn. Since TD-2281 fix `15fbb6a`
 * a pending SINGLE reveal also arrives as `[REVEAL_STARTED]` (Surfe outran the
 * sync window), so the copy stays count-agnostic (one contact or many).
 * Two channels cooperate:
 * - Direct job polling (this card, 2s): live "X of Y" progress from
 *   `GET /enrichment/job/{id}/status` — display only.
 * - The agent poll loop (RevealPollCoordinator, ~20s hidden
 *   CHECK_REVEAL_RESULTS): the contract-faithful backbone that produces the
 *   final render turn with the results table.
 *
 * Fast-finish: when the direct poll sees the pending→terminal transition, the
 * card fires ONE manual CHECK so the agent presents results immediately
 * instead of up to 20s later (the adapter resets the coordinator timer so no
 * duplicate check fires). A job that is already terminal on first fetch —
 * e.g. the card re-mounted from the local cache on an old chat — must NOT
 * fire it: that would append a duplicate results turn to a finished thread.
 */
const RevealProgressCard = ({ jobId, onControlReply = null, onRevealComplete = null }) => {
  const { status, job } = useEnrichmentJobPolling(jobId);
  const sawPendingRef = useRef(false);
  const checkFiredRef = useRef(false);
  const completeFiredRef = useRef(false);

  if (status === 'pending') {
    sawPendingRef.current = true;
  }

  const isRealFailure = status === 'failed' && job?.status === 'failed';
  const isTerminal = status === 'completed' || isRealFailure;

  useEffect(() => {
    if (!isTerminal || !onControlReply) return;
    if (!sawPendingRef.current || checkFiredRef.current) return;
    checkFiredRef.current = true;
    onControlReply(buildRevealPollMessage(jobId));
  }, [isTerminal, onControlReply, jobId]);

  // TD-2293 (#2a): reconcile the enrichment credit chip once the reveal job
  // settles. Per TD-2281's async fix the charge lands with the value on
  // completion (webhook), so the chip's mount-time /subscription-status read is
  // stale until now. Fires ONCE, only on a live pending→terminal transition —
  // a reload of an already-finished job (sawPendingRef false) needs no refetch
  // (its mount read already reflects the settled balance).
  useEffect(() => {
    if (!isTerminal || !onRevealComplete) return;
    if (!sawPendingRef.current || completeFiredRef.current) return;
    completeFiredRef.current = true;
    onRevealComplete();
  }, [isTerminal, onRevealComplete]);

  const total = job?.totalContacts ?? null;
  const enriched = job?.enrichedCount ?? 0;
  const percent = total ? Math.min(100, Math.round((enriched / total) * 100)) : 0;

  // GA4 (TD-2293): metadata only (status + counts, never values). Fire "started"
  // once when the job is first seen running, and "completed" once on the live
  // pending→terminal transition (a reload of an already-finished job fires
  // neither — sawPendingRef stays false).
  const gaStartedRef = useRef(false);
  const gaCompletedRef = useRef(false);
  useEffect(() => {
    if (status === 'pending' && !gaStartedRef.current) {
      gaStartedRef.current = true;
      eventAgentEnrichmentRevealJobStarted();
    }
    if (isTerminal && sawPendingRef.current && !gaCompletedRef.current) {
      gaCompletedRef.current = true;
      eventAgentEnrichmentRevealJobCompleted(job?.status || status, enriched, total ?? 0);
    }
  }, [status, isTerminal, job, enriched, total]);

  let statusLine;
  if (status === 'completed') {
    statusLine =
      total !== null
        ? `Done — ${enriched} of ${total} revealed. Preparing the results…`
        : 'Done — preparing the results…';
  } else if (isRealFailure) {
    statusLine = 'The reveal failed — you are not charged for unrevealed contacts.';
  } else if (status === 'pending' && total !== null) {
    statusLine = `Revealing… ${enriched} of ${total}`;
  } else {
    // idle (first fetch), poll timeout, or a status-endpoint outage: the agent
    // poll loop still delivers the results turn — stay neutral.
    statusLine = 'Revealing… results will appear here shortly.';
  }

  return (
    <section className="reveal-progress-card" aria-label="Reveal progress">
      <div className="reveal-progress-card-title">Contact reveal</div>
      <output className="reveal-progress-card-status" aria-live="polite">
        {statusLine}
      </output>
      {status === 'pending' && total !== null && (
        <div
          className="reveal-progress-card-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className="reveal-progress-card-bar-fill" style={{ width: `${percent}%` }} />
        </div>
      )}
    </section>
  );
};

RevealProgressCard.propTypes = {
  /** Reveal job id from the [REVEAL_STARTED:<jobId>] tag (single or bulk). */
  jobId: PropTypes.string.isRequired,
  /** Sends the hidden CHECK_REVEAL_RESULTS fast-finish poll. */
  onControlReply: PropTypes.func,
  /** Called once when the job settles live, to reconcile the credit chip. */
  onRevealComplete: PropTypes.func
};

export default RevealProgressCard;
